import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Form, Input, Button } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  postLoginApi,
  postRegisterApi,
  sendSmsCodeApi,
  smsLoginApi,
} from "../../service/login";
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { UserRoleManager } from "../../utils/userRole";
import { InitService, convertNewMenuToOldFormat } from "../../utils/initService";
import { globalStore } from "../../utils/globalStore";
import { getFirstHomeShellNavigationPath } from "../../utils/homeShellSidebar";
import { getMenuPermissionsApi } from "../../service/menu";
import { APP_NAME, COPYRIGHT_TEXT, TAB_BRAND_ICON } from "../../config/constants";
import { MARKETING_PAGE_PATH, PREMIUM_TRANSLATION_FULL_PATH } from "../../utils/menuPathNormalize";
import { LEGAL_DOC_HREFS } from "../../legal/legalDocRoutes";
import { ForgotPasswordModal } from "./ForgotPasswordModal";
import { useAutofillSync } from "../../hooks/useAutofillSync";
import PromotionBanner from "../../components/common/PromotionBanner";
import "../../styles/reset.scss";
import "./AuthPage.scss";

/** 左上角品牌：public/marketing-assets/brand-logo.png */
const AUTH_BRAND_LOGO = TAB_BRAND_ICON;

/** 左侧主视觉：public/img/cover-left.png（由 封面left.png 重命名） */
const AUTH_HERO_IMAGE = "/img/cover-left.png";

/** 短信验证码倒计时秒数（与后端限流一致） */
const SMS_CODE_COUNTDOWN_SECONDS = 60;

/** 业务场景：后台用户短信登录 - 对应后端 SmsSceneEnum.ADMIN_MEMBER_LOGIN = 21 */
const SCENE_ADMIN_SMS_LOGIN = 21;

/** 业务场景：注册 - 对应后端 SmsSceneEnum.MEMBER_REGISTER = 5 */
const SCENE_MEMBER_REGISTER = 5;

interface Props {}

type AuthTab = "login" | "sms-login";

export const AuthPage: FC<Props> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loginForm] = Form.useForm();
  const [smsLoginForm] = Form.useForm();
  const [registerForm] = Form.useForm();

  // 同步浏览器自动填充（密码管理器/记住密码）到 Form 状态
  useAutofillSync(loginForm, ["username", "password"]);
  useAutofillSync(smsLoginForm, ["mobile", "code"]);
  useAutofillSync(registerForm, ["username", "phone", "code", "password", "confirmPassword"]);

  // URL 中可带 ?tab=sms-login 或 ?tab=register，默认 login；register 兼容旧链接并打开弹窗
  const tabFromSearch = (() => {
    const t = searchParams.get("tab");
    if (t === "sms-login") return "sms-login" as const;
    return "login" as const;
  })();
  const [activeTab, setActiveTab] = useState<AuthTab>(tabFromSearch);
  const [registerModalOpen, setRegisterModalOpen] = useState(searchParams.get("tab") === "register");

  const [loginLoading, setLoginLoading] = useState(false);
  const [smsLoginLoading, setSmsLoginLoading] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const smsCountdownTimerRef = useRef<number | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSmsSending, setRegisterSmsSending] = useState(false);
  const [registerSmsCountdown, setRegisterSmsCountdown] = useState(0);
  const registerSmsCountdownTimerRef = useRef<number | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [termsCheckboxGlow, setTermsCheckboxGlow] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const registerTermsRowRef = useRef<HTMLDivElement>(null);

  const { alertConfig, hideAlert, showSuccess, showError } = useAlert();

  useEffect(() => {
    setActiveTab(tabFromSearch);
  }, [tabFromSearch]);

  useEffect(() => {
    if (searchParams.get("tab") === "register") {
      setActiveTab("login");
      setRegisterModalOpen(true);
    }
  }, [searchParams]);

  const syncTabToUrl = useCallback(
    (tab: AuthTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === "sms-login") {
        next.set("tab", "sms-login");
      } else {
        next.delete("tab");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const goTab = useCallback(
    (tab: AuthTab) => {
      setActiveTab(tab);
      syncTabToUrl(tab);
    },
    [syncTabToUrl]
  );

  const openRegisterModal = useCallback(() => {
    setActiveTab("login");
    setRegisterModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("tab", "register");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeRegisterModal = useCallback(() => {
    setRegisterModalOpen(false);
    const next = new URLSearchParams(searchParams);
    if (next.get("tab") === "register") {
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 清理短信倒计时定时器
  useEffect(() => {
    return () => {
      if (smsCountdownTimerRef.current) {
        window.clearInterval(smsCountdownTimerRef.current);
        smsCountdownTimerRef.current = null;
      }
      if (registerSmsCountdownTimerRef.current) {
        window.clearInterval(registerSmsCountdownTimerRef.current);
        registerSmsCountdownTimerRef.current = null;
      }
    };
  }, []);

  // 切换到非验证码登录 tab 时清掉倒计时
  useEffect(() => {
    if (activeTab !== "sms-login" && smsCountdownTimerRef.current) {
      window.clearInterval(smsCountdownTimerRef.current);
      smsCountdownTimerRef.current = null;
      setSmsCountdown(0);
    }
  }, [activeTab]);

  useEffect(() => {
    // 仅当「token 已过期跳转回来」时清理登录态 + 提示；避免已登录用户进入 /login 被误登出
    const tokenExpired = localStorage.getItem("tokenExpired");
    if (tokenExpired === "true") {
      localStorage.removeItem("token");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("tokenExpired");
      showError("登录验证失效，请重新登录");
    }
  }, [showError]);

  useEffect(() => {
    if (activeTab !== "login") return;
    const registerSuccess = localStorage.getItem("registerSuccess");
    const registerSuccessMessage = localStorage.getItem("registerSuccessMessage");
    if (registerSuccess === "true" && registerSuccessMessage) {
      showSuccess(registerSuccessMessage);
      localStorage.removeItem("registerSuccess");
      localStorage.removeItem("registerSuccessMessage");
    }
  }, [activeTab, showSuccess]);


  const playRegisterTermsAttention = useCallback(() => {
    const el = registerTermsRowRef.current;
    if (el) {
      el.classList.remove("auth-page__terms-row--shake");
      void el.offsetWidth;
      el.classList.add("auth-page__terms-row--shake");
      const onEnd = () => {
        el.classList.remove("auth-page__terms-row--shake");
      };
      el.addEventListener("animationend", onEnd, { once: true });
    }
    setTermsCheckboxGlow(true);
  }, []);

  useEffect(() => {
    if (!termsCheckboxGlow) return;
    const tid = window.setTimeout(() => setTermsCheckboxGlow(false), 2800);
    return () => window.clearTimeout(tid);
  }, [termsCheckboxGlow]);

  useEffect(() => {
    if (agreeTerms) setTermsCheckboxGlow(false);
  }, [agreeTerms]);
  /**
   * 提取成功登录后的本地存储与跳转逻辑（账号登录 / 短信登录复用）
   */
  const handleLoginSuccess = useCallback(
    async (data: Login.LoginResponse["data"], fallbackUsername: string) => {
      const { accessToken, refreshToken, expiresTime, userId } = data;
      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("tokenExpiresTime", expiresTime);
      localStorage.setItem("userId", String(userId));
      const userInfo = {
        username: fallbackUsername,
        nickname: fallbackUsername,
      };
      UserRoleManager.updateUserRoleInfo(userInfo);
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
      InitService.clearUserData();

      // 登录后预加载菜单数据，以便跳转后根据菜单算第一个导航路径
      let firstPath = PREMIUM_TRANSLATION_FULL_PATH;
      try {
        const menuRes = await getMenuPermissionsApi();
        if (menuRes?.code === 0 && menuRes.data?.menus?.length) {
          const menuList = menuRes.data.menus.map((m: any) => convertNewMenuToOldFormat(m));
          globalStore.setMenuList(menuList);
          // 同时保存用户信息（接口返回的比本地构造的更完整）
          if (menuRes.data.user) {
            const merged = { ...menuRes.data.user, permissions: menuRes.data.permissions || [] };
            globalStore.setUserInfo(merged);
          }
          const fp = getFirstHomeShellNavigationPath(menuList);
          if (fp) firstPath = fp;
        }
      } catch {
        // 静默失败，走默认路径
      }

      window.location.href = firstPath;
    },
    []
  );

  const onLoginFinish = useCallback(
    async (formData: Login.PostLoginPayloadType) => {
      if (!formData.username || !formData.username.trim()) {
        showError("请输入账号");
        return;
      }
      if (!formData.password || !formData.password.trim()) {
        showError("请输入密码");
        return;
      }

      setLoginLoading(true);
      try {
        const payload: Login.PostLoginPayloadType = {
          username: formData.username,
          password: formData.password,
          rememberMe,
        };
        const response = await postLoginApi(payload);

        if (response.code === 0 && response.data && response.data.accessToken) {
          await handleLoginSuccess(response.data, formData.username);
        } else {
          showError(response.msg || "登录失败，请检查账号、密码或租户信息");
        }
      } catch (error: any) {
        if (error.status === 403) {
          showError("访问被拒绝，请检查用户名和密码是否正确");
        } else if (error.status === 415) {
          showError("请求格式错误，请检查Content-Type设置");
        } else if (error.status === 401) {
          showError("用户名或密码错误");
        } else if (error.status === 404) {
          showError("登录接口不存在，请联系管理员");
        } else if (error.status === "NETWORK_ERROR") {
          showError("网络连接失败，请检查网络连接或服务器状态");
        } else if (error.message && error.message.includes("Failed to fetch")) {
          showError("无法连接到服务器，请检查网络连接");
        } else {
          showError(error.error || error.message || "登录失败，请稍后重试");
        }
      } finally {
        setLoginLoading(false);
      }
    },
    [rememberMe, showError, handleLoginSuccess]
  );

  /** 启动短信验证码倒计时 */
  const startSmsCountdown = useCallback(() => {
    setSmsCountdown(SMS_CODE_COUNTDOWN_SECONDS);
    if (smsCountdownTimerRef.current) {
      window.clearInterval(smsCountdownTimerRef.current);
    }
    smsCountdownTimerRef.current = window.setInterval(() => {
      setSmsCountdown((prev) => {
        if (prev <= 1) {
          if (smsCountdownTimerRef.current) {
            window.clearInterval(smsCountdownTimerRef.current);
            smsCountdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /**
   * 关键：密码管理器/浏览器自动填充不会触发 React onChange，
   * 导致 antd Form 内部值与 DOM 不同步。这里强制从 DOM 读最新值写回 Form。
   * 使用 antd Form 的 getFieldInstance API 直接拿到底层 input DOM 节点，
   * 不依赖 input[name] 选择器（antd Form.Item 不会把 name 透传到 input）。
   */
  const syncFieldFromDom = useCallback((formInstance: any, name: string) => {
    if (!formInstance) return "";
    let el: HTMLInputElement | null = null;
    try {
      const inst = formInstance.getFieldInstance?.(name);
      // antd Input 实例通常有 .input 指向原生 input；也可能直接就是 input
      el = (inst?.input as HTMLInputElement) || (inst as HTMLInputElement) || null;
    } catch {
      /* noop */
    }
    // 兜底：用 id 前缀匹配（项目里 SMS login / register / forgot 表单的 input 都设了 id）
    if (!el && typeof document !== "undefined") {
      const idMap: Record<string, string> = {
        mobile: "auth-smslogin-mobile",
        phone: "auth-reg-phone",
      };
      const id = idMap[name];
      if (id) {
        el = document.getElementById(id) as HTMLInputElement | null;
      }
    }
    if (!el) return "";
    const value = el.value || "";
    const formValue = formInstance.getFieldValue(name);
    if (value && value !== formValue) {
      formInstance.setFieldsValue({ [name]: value });
    }
    return value;
  }, []);

  /** 发送短信验证码（验证码登录 tab） */
  const onSendLoginSmsCode = useCallback(async () => {
    if (smsSending || smsCountdown > 0 || smsLoginLoading) return;
    // 关键：先从 DOM 同步到 Form，再校验，避免自动填充场景下 Form 内部值过时
    const domMobile = syncFieldFromDom(smsLoginForm, "mobile");
    // 强制再写一次，绕过异步时序问题
    if (domMobile) {
      smsLoginForm.setFieldsValue({ mobile: domMobile });
    }
    let mobile = "";
    try {
      const validated = await smsLoginForm.validateFields(["mobile"]);
      mobile = (validated as any).mobile || "";
    } catch {
      // 兜底：直接用 DOM 值，不让前端校验拦截自动填充场景
      if (domMobile) {
        mobile = domMobile;
      } else {
        return;
      }
    }
    // 兜底：自动填充场景下，DOM 有值但 Form 仍可能为空
    if (!mobile && domMobile) {
      mobile = domMobile;
    }
    if (!/^1[3-9]\d{9}$/.test(mobile)) {
      showError("请输入正确的手机号");
      return;
    }
    setSmsSending(true);
    try {
      const res = await sendSmsCodeApi({ mobile, scene: SCENE_ADMIN_SMS_LOGIN });
      if (res && res.code === 0) {
        showSuccess("验证码已发送，请注意查收短信");
        startSmsCountdown();
      } else {
        showError(res?.msg || "验证码发送失败，请稍后重试");
      }
    } catch (err: any) {
      showError(
        err?.response?.msg ||
          err?.msg ||
          err?.detail ||
          err?.message ||
          "验证码发送失败，请稍后重试"
      );
    } finally {
      setSmsSending(false);
    }
  }, [smsSending, smsCountdown, smsLoginLoading, smsLoginForm, showError, showSuccess, startSmsCountdown, syncFieldFromDom]);

  /** 提交短信验证码登录 */
  const onSmsLoginFinish = useCallback(
    async (formData: Login.PostSmsLoginPayloadType) => {
      if (!formData.mobile || !/^1[3-9]\d{9}$/.test(formData.mobile)) {
        showError("请输入正确的手机号");
        return;
      }
      if (!formData.code || !formData.code.trim()) {
        showError("请输入短信验证码");
        return;
      }
      setSmsLoginLoading(true);
      try {
        const response = await smsLoginApi({
          mobile: formData.mobile,
          code: formData.code.trim(),
        });
        if (response.code === 0 && response.data && response.data.accessToken) {
          await handleLoginSuccess(response.data, formData.mobile);
        } else {
          showError(response.msg || "登录失败，请检查手机号与验证码");
        }
      } catch (error: any) {
        if (error.status === 401) {
          showError("验证码错误或已过期");
        } else if (error.status === 404) {
          showError("登录接口不存在，请联系管理员");
        } else {
          showError(error.error || error.message || "登录失败，请稍后重试");
        }
      } finally {
        setSmsLoginLoading(false);
      }
    },
    [showError, handleLoginSuccess]
  );

  /** 启动注册场景的短信验证码倒计时 */
  const startRegisterSmsCountdown = useCallback(() => {
    setRegisterSmsCountdown(SMS_CODE_COUNTDOWN_SECONDS);
    if (registerSmsCountdownTimerRef.current) {
      window.clearInterval(registerSmsCountdownTimerRef.current);
    }
    registerSmsCountdownTimerRef.current = window.setInterval(() => {
      setRegisterSmsCountdown((prev) => {
        if (prev <= 1) {
          if (registerSmsCountdownTimerRef.current) {
            window.clearInterval(registerSmsCountdownTimerRef.current);
            registerSmsCountdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /** 发送注册场景的短信验证码 */
  const onSendRegisterSmsCode = useCallback(async () => {
    if (registerSmsSending || registerSmsCountdown > 0 || registerLoading) return;
    // 关键：先从 DOM 同步到 Form，再校验，避免自动填充场景下 Form 内部值过时
    const domMobile = syncFieldFromDom(registerForm, "phone");
    // 强制再写一次，绕过异步时序问题
    if (domMobile) {
      registerForm.setFieldsValue({ phone: domMobile });
    }
    let mobile = "";
    try {
      const validated = await registerForm.validateFields(["phone"]);
      mobile = (validated as any).phone || "";
    } catch {
      // 兜底：直接用 DOM 值，不让前端校验拦截自动填充场景
      if (domMobile) {
        mobile = domMobile;
      } else {
        return;
      }
    }
    // 兜底：自动填充场景下，DOM 有值但 Form 仍可能为空
    if (!mobile && domMobile) {
      mobile = domMobile;
    }
    if (!/^1[3-9]\d{9}$/.test(mobile)) {
      showError("请输入正确的手机号");
      return;
    }
    setRegisterSmsSending(true);
    try {
      const res = await sendSmsCodeApi({ mobile, scene: SCENE_MEMBER_REGISTER });
      if (res && res.code === 0) {
        showSuccess("验证码已发送，请注意查收短信");
        startRegisterSmsCountdown();
      } else {
        showError(res?.msg || "验证码发送失败，请稍后重试");
      }
    } catch (err: any) {
      showError(
        err?.response?.msg ||
          err?.msg ||
          err?.detail ||
          err?.message ||
          "验证码发送失败，请稍后重试"
      );
    } finally {
      setRegisterSmsSending(false);
    }
  }, [registerSmsSending, registerSmsCountdown, registerLoading, registerForm, showError, showSuccess, startRegisterSmsCountdown, syncFieldFromDom]);

  const onRegisterFinish = useCallback(
    async (formData: {
      username?: string;
      phone?: string;
      code?: string;
      password?: string;
      confirmPassword?: string;
    }) => {
      if (!formData.username || !formData.username.trim()) {
        showError("请输入账号");
        return;
      }
      if (!formData.phone || !formData.phone.trim()) {
        showError("请输入手机号");
        return;
      }
      if (!formData.code || !formData.code.trim()) {
        showError("请输入短信验证码");
        return;
      }
      if (!formData.password || !formData.password.trim()) {
        showError("请输入密码");
        return;
      }
      if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
        showError("请重复输入密码");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        showError("两次输入的密码不一致");
        return;
      }

      setRegisterLoading(true);
      try {
        const registerData = {
          username: formData.username,
          phone: formData.phone,
          password: formData.password,
          code: formData.code!.trim(),
        };
        const response = await postRegisterApi(registerData);
        if (response && response.code === 0) {
          localStorage.setItem("registerSuccess", "true");
          localStorage.setItem("registerSuccessMessage", "注册成功！请登录您的账户");
          registerForm.resetFields();
          setAgreeTerms(false);
          setActiveTab("login");
          closeRegisterModal();
        } else {
          showError(response?.msg || "注册失败，请稍后重试");
        }
      } catch (error: any) {
        showError(
          error?.response?.msg || error?.msg || error?.detail || error?.message || "注册失败，请稍后重试"
        );
      } finally {
        setRegisterLoading(false);
      }
    },
    [registerForm, showError, closeRegisterModal]
  );


  const handleRegisterFormFinish = useCallback(
    async (formData: {
      username?: string;
      phone?: string;
      code?: string;
      password?: string;
      confirmPassword?: string;
    }) => {
      if (!agreeTerms) {
        playRegisterTermsAttention();
        return;
      }
      await onRegisterFinish(formData);
    },
    [agreeTerms, onRegisterFinish, playRegisterTermsAttention]
  );
  const handleForgotPassword = useCallback(() => {
    setForgotPasswordOpen(true);
  }, []);

  const loginPanel = (
    <div>
      <Form form={loginForm} onFinish={onLoginFinish} layout="vertical" requiredMark={false}>
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-login-username">
            账号
          </label>
          <Form.Item
            name="username"
            rules={[
              { required: true, message: "请输入账号" },
              { min: 1, message: "账号不能为空" },
            ]}
          >
            <Input
              id="auth-login-username"
              placeholder="请输入账号"
              disabled={loginLoading}
              autoComplete="username"
            />
          </Form.Item>
        </div>
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-login-password">
            密码
          </label>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: "请输入密码" },
              { min: 1, message: "密码不能为空" },
            ]}
          >
            <div className="auth-page__pwd-wrap">
              <Input
                id="auth-login-password"
                type={showLoginPassword ? "text" : "password"}
                placeholder="请输入密码"
                disabled={loginLoading}
                autoComplete="current-password"
                style={{ paddingRight: "3rem" }}
              />
              <button
                type="button"
                className="auth-page__pwd-toggle"
                aria-label={showLoginPassword ? "隐藏密码" : "显示密码"}
                onClick={() => setShowLoginPassword((v) => !v)}
              >
                {showLoginPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
          </Form.Item>
        </div>
        <div className="auth-page__options">
          <div className="auth-page__options-start">
            <div className="auth-page__chk">
              <input
                type="checkbox"
                id="rememberMe"
                className="auth-page__chk-input"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loginLoading}
              />
              <span className="auth-page__chk-mark" aria-hidden>
                ✓
              </span>
            </div>
            <label htmlFor="rememberMe" className="auth-page__inline-label">
              记住我
            </label>
          </div>
          <a
            href="#"
            className="auth-page__link"
            onClick={(e) => {
              e.preventDefault();
              handleForgotPassword();
            }}
          >
            忘记密码?
          </a>
        </div>
        <Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            className="auth-page__submit"
            disabled={loginLoading}
            loading={loginLoading}
          >
            {loginLoading ? "登录中..." : "登录"}
          </Button>
        </Form.Item>
      </Form>
      <footer className="auth-page__footer">
        <p className="auth-page__footer-text">
          没有账号？
          <button type="button" className="auth-page__footer-action" onClick={openRegisterModal}>
            立即注册
          </button>
        </p>
      </footer>
    </div>
  );

  const smsLoginPanel = (
    <div>
      <Form
        form={smsLoginForm}
        onFinish={onSmsLoginFinish}
        layout="vertical"
        requiredMark={false}
      >
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-smslogin-mobile">
            手机号
          </label>
          <Form.Item
            name="mobile"
            rules={[
              { required: true, message: "请输入手机号" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号格式" },
            ]}
          >
            <Input
              id="auth-smslogin-mobile"
              type="tel"
              placeholder="请输入手机号"
              disabled={smsLoginLoading}
              autoComplete="tel"
              maxLength={11}
            />
          </Form.Item>
        </div>        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-smslogin-code">
            验证码
          </label>
          <div className="auth-page__sms-row">
            <Form.Item
              name="code"
              rules={[
                { required: true, message: "请输入短信验证码" },
              ]}
            >
              <Input
                id="auth-smslogin-code"
                placeholder="请输入短信验证码"
                disabled={smsLoginLoading}
                maxLength={8}
                autoComplete="one-time-code"
              />
            </Form.Item>
            <button
              type="button"
              className="auth-page__send-code-btn"
              onClick={onSendLoginSmsCode}
              disabled={smsSending || smsCountdown > 0 || smsLoginLoading}
            >
              {smsCountdown > 0
                ? `${smsCountdown}s 后重试`
                : smsSending
                ? "发送中..."
                : "获取验证码"}
            </button>
          </div>
        </div>        <Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            className="auth-page__submit"
            disabled={smsLoginLoading}
            loading={smsLoginLoading}
          >
            {smsLoginLoading ? "登录中..." : "登录"}
          </Button>
        </Form.Item>
      </Form>
      <footer className="auth-page__footer">
        <p className="auth-page__footer-text">
          没有账号？
          <button
            type="button"
            className="auth-page__footer-action"
            onClick={openRegisterModal}
          >
            立即注册
          </button>
        </p>
      </footer>
    </div>
  );

  const registerPanel = (
    <div>
      <header className="auth-page__head">
        <h1 className="auth-page__head-title" id="auth-register-title">创建账号</h1>
        <p className="auth-page__head-desc subtitle">注册后即可使用全部功能</p>
      </header>
      <Form form={registerForm} onFinish={handleRegisterFormFinish} layout="vertical" requiredMark={false}>
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-reg-username">
            用户名
          </label>
          <Form.Item
            name="username"
            rules={[
              { required: true, message: "请输入账号" },
              {
                pattern: /^[a-zA-Z0-9]+$/,
                message: "用户名仅支持英文字母与数字组成",
              },
              { min: 4, message: "账号不少于四位" },
              { max: 20, message: "账号最多20个字符" },
            ]}
          >
            <Input
              id="auth-reg-username"
              placeholder="请输入用户名"
              disabled={registerLoading}
              autoComplete="username"
            />
          </Form.Item>
        </div>
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-reg-phone">
            手机号
          </label>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: "请输入手机号" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号格式" },
            ]}
          >
            <Input
              id="auth-reg-phone"
              type="tel"
              placeholder="请输入手机号"
              disabled={registerLoading}
              autoComplete="tel"
              maxLength={11}
            />
          </Form.Item>
        </div>        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-reg-code">
            短信验证码
          </label>
          <div className="auth-page__sms-row">
            <Form.Item
              name="code"
              rules={[
                { required: true, message: "请输入短信验证码" },
              ]}
            >
              <Input
                id="auth-reg-code"
                placeholder="请输入短信验证码"
                disabled={registerLoading}
                maxLength={8}
                autoComplete="one-time-code"
              />
            </Form.Item>
            <button
              type="button"
              className="auth-page__send-code-btn"
              onClick={onSendRegisterSmsCode}
              disabled={registerSmsSending || registerSmsCountdown > 0 || registerLoading}
            >
              {registerSmsCountdown > 0
                ? `${registerSmsCountdown}s 后重试`
                : registerSmsSending
                ? "发送中..."
                : "获取验证码"}
            </button>
          </div>
        </div>        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-reg-password">
            设置密码
          </label>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: "请输入密码" },
              { min: 6, message: "密码至少6个字符" },
              { max: 20, message: "密码最多20个字符" },
            ]}
          >
            <div className="auth-page__pwd-wrap">
              <Input
                id="auth-reg-password"
                type={showRegisterPassword ? "text" : "password"}
                placeholder="请输入密码（至少6位）"
                disabled={registerLoading}
                autoComplete="new-password"
                style={{ paddingRight: "3rem" }}
              />
              <button
                type="button"
                className="auth-page__pwd-toggle"
                aria-label={showRegisterPassword ? "隐藏密码" : "显示密码"}
                onClick={() => setShowRegisterPassword((v) => !v)}
              >
                {showRegisterPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
          </Form.Item>
        </div>
        <div className="auth-page__field">
          <label className="auth-page__label" htmlFor="auth-reg-confirm">
            确认密码
          </label>
          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: "请重复输入密码" },
              { min: 6, message: "密码至少6个字符" },
              { max: 20, message: "密码最多20个字符" },
            ]}
          >
            <Input
              id="auth-reg-confirm"
              type="password"
              placeholder="请再次输入密码"
              disabled={registerLoading}
              autoComplete="new-password"
            />
          </Form.Item>
        </div>
        <div ref={registerTermsRowRef} className="auth-page__row auth-page__terms-row">
          <div className={`auth-page__chk${termsCheckboxGlow ? " auth-page__chk--attention" : ""}`}>
            <input
              type="checkbox"
              id="auth-terms"
              className="auth-page__chk-input"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              disabled={registerLoading}
            />
            <span className="auth-page__chk-mark" aria-hidden>
              ✓
            </span>
          </div>
          <label htmlFor="auth-terms" className="auth-page__terms-label">
            我已阅读并同意{" "}
            <a href={LEGAL_DOC_HREFS.userAgreement} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              用户协议
            </a>{" "}
            及{" "}
            <a href={LEGAL_DOC_HREFS.privacyPolicy} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              隐私政策
            </a>
          </label>
        </div>
        <Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            className="auth-page__submit"
            disabled={registerLoading}
            loading={registerLoading}
          >
            {registerLoading ? "注册中..." : "立即注册"}
          </Button>
        </Form.Item>
      </Form>
      <footer className="auth-page__footer">
        <p className="auth-page__footer-text">
          已有账号？
          <button type="button" className="auth-page__footer-action" onClick={closeRegisterModal}>
            立即登录
          </button>
        </p>
      </footer>
    </div>
  );

  return (
    <div className="auth-page-root">
      <PromotionBanner ignoreAuth />
      <div className="auth-page">
      <div className="auth-page__bg" aria-hidden>
        <div className="auth-page__bg-blob1" />
        <div className="auth-page__bg-blob2" />
      </div>
      <div className="auth-page__topbar">
        <Link to={MARKETING_PAGE_PATH} className="auth-page__topbar-inner">
          <img src={AUTH_BRAND_LOGO} alt={APP_NAME} className="auth-page__topbar-logo" width={36} height={36} />
          <span className="auth-page__topbar-name">{APP_NAME}</span>
        </Link>
      </div>

      <div className="auth-page__content">
      <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.LOGIN} />

      <main className="auth-page__main">
        <section className="auth-page__left" aria-label="品牌与插图">
          <div className="auth-page__hero-wrap">
            <img className="auth-page__hero-img" src={AUTH_HERO_IMAGE} alt="" />
          </div>
          <div className="auth-page__brand-text">
            <p className="auth-page__brand-kicker">Design & Create</p>
            <h3 className="auth-page__brand-title">让声音，成为创作的起点</h3>
          </div>
        </section>

        <section className="auth-page__right">
          <div className="auth-page__panel">
            <header className="auth-page__login-head">
              <h1 className="auth-page__login-title">登录</h1>
              <p className="auth-page__login-desc">使用您的账号继续</p>
            </header>
            <div className="auth-page__tabs" role="tablist" aria-label="登录方式">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "login"}
                  className={`auth-page__tab${activeTab === "login" ? " auth-page__tab--active" : ""}`}
                  onClick={() => goTab("login")}
                >
                  密码登录
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "sms-login"}
                  className={`auth-page__tab${activeTab === "sms-login" ? " auth-page__tab--active" : ""}`}
                  onClick={() => goTab("sms-login")}
                >
                  验证码登录
                </button>
            </div>
            <div className="auth-page__panel-body">
              {activeTab === "login" ? loginPanel : smsLoginPanel}
            </div>
            <div className="auth-page__marketing">
              <Link to={MARKETING_PAGE_PATH}>返回官网</Link>
            </div>
          </div>
        </section>
      </main>

      <div className="auth-page__copyright">{COPYRIGHT_TEXT}</div>
      <div
        className={`auth-modal-overlay${registerModalOpen ? " show" : ""}`}
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !registerLoading) {
            closeRegisterModal();
          }
        }}
      >
        <div className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-register-title">
          <button
            type="button"
            className="auth-modal-close"
            onClick={closeRegisterModal}
            disabled={registerLoading}
            aria-label="关闭"
          >
            ×
          </button>
          {registerPanel}
        </div>
      </div>

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        showSuccess={showSuccess}
        showError={showError}
      />
      </div>
    </div>
    </div>
  );
};
